import { useState, useRef } from "react";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronLeft, Download, Upload, AlertCircle, CheckCircle } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { useClasses } from "@/lib/api/classes/hooks";
import { useCategories } from "@/lib/api/vocabulary/hooks";
import { useBulkCreateTasks } from "@/lib/api/tasks/hooks/use-bulk-create-tasks";
import type { CreateTaskRequest } from "@/lib/api/tasks/types";
import type { Class } from "@/lib/api/classes/types";
import type { Category } from "@/lib/api/vocabulary/types";

// ---------------------------------------------------------------------------
// 타입 정의
// ---------------------------------------------------------------------------

type TabType = "quiz" | "general";

interface QuizRow {
  id: number;
  date: string;
  className: string;
  categoryName: string;
  startPage: string;
  endPage: string;
  questionCount: string;
  title: string;
  // 검증 에러
  errors: string[];
  // 서버 에러 (422)
  serverError?: string;
}

interface GeneralRow {
  id: number;
  date: string;
  endDate: string;
  className: string;
  title: string;
  description: string;
  // 검증 에러
  errors: string[];
  // 서버 에러 (422)
  serverError?: string;
}

// ---------------------------------------------------------------------------
// CSV 파싱 유틸
// ---------------------------------------------------------------------------

function parseCSVLine(line: string): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      fields.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  fields.push(current.trim());
  return fields;
}

function isValidDate(value: string): boolean {
  if (!value) return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !isNaN(Date.parse(value));
}

// ---------------------------------------------------------------------------
// 행 검증
// ---------------------------------------------------------------------------

function validateQuizRow(
  row: QuizRow,
  classes: Class[] | undefined,
  categories: { _id: string; name: string }[] | undefined
): string[] {
  const errors: string[] = [];

  if (!isValidDate(row.date)) errors.push("date: 유효한 날짜 형식(YYYY-MM-DD)이 아닙니다.");
  if (!row.className.trim()) errors.push("className: 반 이름이 비어있습니다.");
  if (!row.categoryName.trim()) errors.push("categoryName: 카테고리 이름이 비어있습니다.");
  if (!row.startPage || isNaN(Number(row.startPage)) || Number(row.startPage) < 1)
    errors.push("startPage: 유효한 숫자여야 합니다.");
  if (!row.endPage || isNaN(Number(row.endPage)) || Number(row.endPage) < 1)
    errors.push("endPage: 유효한 숫자여야 합니다.");
  if (
    !row.questionCount ||
    isNaN(Number(row.questionCount)) ||
    Number(row.questionCount) < 1
  )
    errors.push("questionCount: 유효한 숫자여야 합니다.");

  if (classes && row.className.trim()) {
    const matched = classes.find((c) => c.name === row.className.trim());
    if (!matched) errors.push(`className: "${row.className}" 반을 찾을 수 없습니다.`);
  }

  if (categories && row.categoryName.trim()) {
    const matched = categories.find((c) => c.name === row.categoryName.trim());
    if (!matched)
      errors.push(`categoryName: "${row.categoryName}" 카테고리를 찾을 수 없습니다.`);
  }

  return errors;
}

function validateGeneralRow(
  row: GeneralRow,
  classes: Class[] | undefined
): string[] {
  const errors: string[] = [];

  if (!isValidDate(row.date)) errors.push("date: 유효한 날짜 형식(YYYY-MM-DD)이 아닙니다.");
  if (row.endDate && !isValidDate(row.endDate))
    errors.push("endDate: 유효한 날짜 형식(YYYY-MM-DD)이 아닙니다.");
  if (!row.className.trim()) errors.push("className: 반 이름이 비어있습니다.");
  if (!row.title.trim()) errors.push("title: 제목이 비어있습니다.");

  if (classes && row.className.trim()) {
    const matched = classes.find((c) => c.name === row.className.trim());
    if (!matched) errors.push(`className: "${row.className}" 반을 찾을 수 없습니다.`);
  }

  return errors;
}

// ---------------------------------------------------------------------------
// 템플릿 CSV 컨텐츠
// ---------------------------------------------------------------------------

const QUIZ_TEMPLATE = `date,className,categoryName,startPage,endPage,questionCount,title
2026-05-20,A반,영단어 기초,1,5,10,5월 1주차 퀴즈
2026-05-22,A반,영단어 기초,6,10,10,5월 2주차 퀴즈
2026-05-20,B반,영단어 심화,1,3,15,
`;

const GENERAL_TEMPLATE = `date,endDate,className,title,description
2026-05-20,,A반,교과서 1단원 풀기,p.10~p.25 문제 풀이
2026-05-20,2026-05-27,A반,5월 4주차 숙제,교과서 1~3단원 복습 (기간 과제)
2026-05-21,,B반,단어 외우기,
`;

function downloadCSV(content: string, filename: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

// ---------------------------------------------------------------------------
// 메인 컴포넌트
// ---------------------------------------------------------------------------

function TasksUploadPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("quiz");

  const [quizRows, setQuizRows] = useState<QuizRow[]>([]);
  const [generalRows, setGeneralRows] = useState<GeneralRow[]>([]);

  const quizFileRef = useRef<HTMLInputElement>(null);
  const generalFileRef = useRef<HTMLInputElement>(null);

  const { data: classes } = useClasses();
  const { data: categories } = useCategories();
  const { mutateAsync: bulkCreate, isPending } = useBulkCreateTasks();

  // -----------------------------------------------------------------------
  // CSV 파싱
  // -----------------------------------------------------------------------

  const parseQuizCSV = (text: string) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) {
      toast.error("CSV 파일이 비어있습니다.");
      return;
    }

    const headers = parseCSVLine(lines[0]).map((h) =>
      h.toLowerCase().replace(/\(.*?\)/g, "").trim()
    );

    const dateIdx = headers.findIndex((h) => h.includes("date") && !h.includes("end"));
    const classIdx = headers.findIndex((h) => h.includes("class"));
    const catIdx = headers.findIndex((h) => h.includes("category"));
    const startIdx = headers.findIndex((h) => h.includes("startpage"));
    const endPageIdx = headers.findIndex((h) => h.includes("endpage"));
    const qcIdx = headers.findIndex((h) => h.includes("question"));
    const titleIdx = headers.findIndex((h) => h.includes("title"));

    if (dateIdx < 0 || classIdx < 0 || catIdx < 0 || startIdx < 0 || endPageIdx < 0 || qcIdx < 0) {
      toast.error("퀴즈 CSV에 필수 컬럼이 없습니다. (date, className, categoryName, startPage, endPage, questionCount)");
      return;
    }

    const rows: QuizRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      const cols = parseCSVLine(line);
      const row: QuizRow = {
        id: i,
        date: cols[dateIdx] ?? "",
        className: cols[classIdx] ?? "",
        categoryName: cols[catIdx] ?? "",
        startPage: cols[startIdx] ?? "",
        endPage: cols[endPageIdx] ?? "",
        questionCount: cols[qcIdx] ?? "",
        title: titleIdx >= 0 ? (cols[titleIdx] ?? "") : "",
        errors: [],
      };
      row.errors = validateQuizRow(row, classes, categories);
      rows.push(row);
    }
    setQuizRows(rows);
  };

  const parseGeneralCSV = (text: string) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) {
      toast.error("CSV 파일이 비어있습니다.");
      return;
    }

    const headers = parseCSVLine(lines[0]).map((h) =>
      h.toLowerCase().replace(/\(.*?\)/g, "").trim()
    );

    const dateIdx = headers.findIndex((h) => h === "date");
    const endDateIdx = headers.findIndex((h) => h === "enddate");
    const classIdx = headers.findIndex((h) => h.includes("class"));
    const titleIdx = headers.findIndex((h) => h.includes("title"));
    const descIdx = headers.findIndex((h) => h.includes("description"));

    if (dateIdx < 0 || classIdx < 0 || titleIdx < 0) {
      toast.error("일반 CSV에 필수 컬럼이 없습니다. (date, className, title)");
      return;
    }

    const rows: GeneralRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      const cols = parseCSVLine(line);
      const row: GeneralRow = {
        id: i,
        date: cols[dateIdx] ?? "",
        endDate: endDateIdx >= 0 ? (cols[endDateIdx] ?? "") : "",
        className: cols[classIdx] ?? "",
        title: cols[titleIdx] ?? "",
        description: descIdx >= 0 ? (cols[descIdx] ?? "") : "",
        errors: [],
      };
      row.errors = validateGeneralRow(row, classes);
      rows.push(row);
    }
    setGeneralRows(rows);
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    tab: TabType
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const buffer = ev.target?.result as ArrayBuffer;
      const utf8Decoder = new TextDecoder("utf-8");
      const utf8Text = utf8Decoder.decode(buffer);
      const text = utf8Text.includes("�")
        ? new TextDecoder("euc-kr").decode(buffer)
        : utf8Text;

      if (tab === "quiz") parseQuizCSV(text);
      else parseGeneralCSV(text);
    };
    reader.readAsArrayBuffer(file);
    // 같은 파일 재업로드 허용
    e.target.value = "";
  };

  // -----------------------------------------------------------------------
  // 셀 편집
  // -----------------------------------------------------------------------

  const updateQuizRow = (id: number, field: keyof Omit<QuizRow, "id" | "errors" | "serverError">, value: string) => {
    setQuizRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const updated = { ...row, [field]: value };
        updated.errors = validateQuizRow(updated, classes, categories);
        updated.serverError = undefined;
        return updated;
      })
    );
  };

  const updateGeneralRow = (id: number, field: keyof Omit<GeneralRow, "id" | "errors" | "serverError">, value: string) => {
    setGeneralRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const updated = { ...row, [field]: value };
        updated.errors = validateGeneralRow(updated, classes);
        updated.serverError = undefined;
        return updated;
      })
    );
  };

  // -----------------------------------------------------------------------
  // 업로드
  // -----------------------------------------------------------------------

  const buildQuizTaskPayload = (row: QuizRow): CreateTaskRequest | null => {
    const cls = classes?.find((c: Class) => c.name === row.className.trim());
    const cat = categories?.find((c: Category) => c.name === row.categoryName.trim());
    if (!cls || !cat) return null;

    return {
      type: "quiz",
      title: row.title.trim() || `${row.date} ${row.className} 퀴즈`,
      assigneeType: "class",
      classId: cls._id,
      date: row.date,
      categoryId: cat._id,
      startPage: Number(row.startPage),
      endPage: Number(row.endPage),
      questionCount: Number(row.questionCount),
    };
  };

  const buildGeneralTaskPayload = (row: GeneralRow): CreateTaskRequest | null => {
    const cls = classes?.find((c: Class) => c.name === row.className.trim());
    if (!cls) return null;

    const hasEndDate = row.endDate.trim() !== "";
    return {
      type: "general",
      title: row.title.trim(),
      description: row.description.trim() || undefined,
      assigneeType: "class",
      classId: cls._id,
      startDate: row.date,
      endDate: hasEndDate ? row.endDate : row.date,
    };
  };

  const handleUpload = async () => {
    const rows = activeTab === "quiz" ? quizRows : generalRows;
    const hasErrors = rows.some((r) => r.errors.length > 0);
    if (hasErrors) {
      toast.error("검증 에러가 있는 행을 수정해주세요.");
      return;
    }

    const tasks =
      activeTab === "quiz"
        ? quizRows.map(buildQuizTaskPayload).filter((t): t is CreateTaskRequest => t !== null)
        : generalRows
            .map(buildGeneralTaskPayload)
            .filter((t): t is CreateTaskRequest => t !== null);

    if (tasks.length === 0) {
      toast.error("업로드할 태스크가 없습니다.");
      return;
    }

    try {
      await bulkCreate({ tasks });
      toast.success(`${tasks.length}개 태스크가 등록되었습니다.`);
      navigate({ to: "/tasks" });
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { status?: number; data?: { errors?: { index: number; message: string }[] } };
      };

      if (axiosError.response?.status === 422 && axiosError.response.data?.errors) {
        const serverErrors = axiosError.response.data.errors;
        if (activeTab === "quiz") {
          setQuizRows((prev) =>
            prev.map((row, idx) => {
              const serverErr = serverErrors.find((e) => e.index === idx);
              return serverErr ? { ...row, serverError: serverErr.message } : row;
            })
          );
        } else {
          setGeneralRows((prev) =>
            prev.map((row, idx) => {
              const serverErr = serverErrors.find((e) => e.index === idx);
              return serverErr ? { ...row, serverError: serverErr.message } : row;
            })
          );
        }
        toast.error("일부 행에서 서버 검증 에러가 발생했습니다. 해당 행을 확인해주세요.");
      } else {
        toast.error("업로드 중 오류가 발생했습니다.");
      }
    }
  };

  // -----------------------------------------------------------------------
  // 렌더
  // -----------------------------------------------------------------------

  const quizHasErrors = quizRows.some((r) => r.errors.length > 0 || r.serverError);
  const generalHasErrors = generalRows.some((r) => r.errors.length > 0 || r.serverError);
  const currentHasErrors = activeTab === "quiz" ? quizHasErrors : generalHasErrors;
  const currentRows = activeTab === "quiz" ? quizRows : generalRows;

  return (
    <PageContainer>
      <Header
        title="CSV 일괄 등록"
        left={
          <Link to="/tasks" className="p-2 -ml-2">
            <ChevronLeft className="size-6" />
          </Link>
        }
      />

      <div className="space-y-6">
        {/* 탭 */}
        <div className="flex rounded-lg border border-input overflow-hidden w-fit">
          <button
            onClick={() => setActiveTab("quiz")}
            className={`px-6 py-2.5 text-sm font-medium transition-colors ${
              activeTab === "quiz"
                ? "bg-primary text-primary-foreground"
                : "bg-background hover:bg-muted"
            }`}
          >
            Quiz CSV
          </button>
          <button
            onClick={() => setActiveTab("general")}
            className={`px-6 py-2.5 text-sm font-medium transition-colors border-l border-input ${
              activeTab === "general"
                ? "bg-primary text-primary-foreground"
                : "bg-background hover:bg-muted"
            }`}
          >
            General CSV
          </button>
        </div>

        {/* 컬럼 안내 */}
        <div className="rounded-lg border border-input bg-muted/40 p-4 text-sm space-y-2">
          {activeTab === "quiz" ? (
            <>
              <div className="font-medium">Quiz CSV 컬럼 가이드</div>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                <li><code className="font-mono">date</code> (필수) — 퀴즈 날짜, <code className="font-mono">YYYY-MM-DD</code></li>
                <li><code className="font-mono">className</code> (필수) — 등록된 반 이름과 정확히 일치</li>
                <li><code className="font-mono">categoryName</code> (필수) — 등록된 어휘 카테고리 이름</li>
                <li><code className="font-mono">startPage</code>, <code className="font-mono">endPage</code> (필수) — 출제 범위 페이지</li>
                <li><code className="font-mono">questionCount</code> (필수) — 출제 문제 수</li>
                <li><code className="font-mono">title</code> (선택) — 비워두면 자동 생성</li>
              </ul>
            </>
          ) : (
            <>
              <div className="font-medium">General CSV 컬럼 가이드</div>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                <li><code className="font-mono">date</code> (필수) — 시작 날짜, <code className="font-mono">YYYY-MM-DD</code></li>
                <li><code className="font-mono">endDate</code> (선택) — 비워두면 단일 날짜 태스크, 채우면 기간 태스크</li>
                <li><code className="font-mono">className</code> (필수) — 등록된 반 이름과 정확히 일치</li>
                <li><code className="font-mono">title</code> (필수) — 태스크 제목</li>
                <li><code className="font-mono">description</code> (선택) — 상세 설명</li>
              </ul>
            </>
          )}
        </div>

        {/* 액션 버튼 */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => {
              if (activeTab === "quiz") {
                downloadCSV(QUIZ_TEMPLATE, "quiz_template.csv");
              } else {
                downloadCSV(GENERAL_TEMPLATE, "general_template.csv");
              }
            }}
          >
            <Download className="size-4 mr-2" />
            템플릿 다운로드
          </Button>

          <div>
            <input
              type="file"
              accept=".csv"
              ref={activeTab === "quiz" ? quizFileRef : generalFileRef}
              onChange={(e) => handleFileChange(e, activeTab)}
              className="hidden"
            />
            <Button
              onClick={() => {
                if (activeTab === "quiz") quizFileRef.current?.click();
                else generalFileRef.current?.click();
              }}
            >
              <Upload className="size-4 mr-2" />
              파일 선택
            </Button>
          </div>
        </div>

        {/* 컬럼 안내 */}
        <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
          {activeTab === "quiz" ? (
            <span>
              <strong>필수:</strong> date(YYYY-MM-DD), className, categoryName, startPage, endPage, questionCount &nbsp;|&nbsp;
              <strong>선택:</strong> title
            </span>
          ) : (
            <span>
              <strong>필수:</strong> date(YYYY-MM-DD), className, title &nbsp;|&nbsp;
              <strong>선택:</strong> endDate(YYYY-MM-DD), description &nbsp; (endDate 없으면 당일 단일 태스크)
            </span>
          )}
        </div>

        {/* 미리보기 테이블 */}
        {currentRows.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {currentRows.length}개 행 파싱됨
              </span>
              {currentHasErrors ? (
                <span className="flex items-center gap-1 text-sm text-destructive">
                  <AlertCircle className="size-4" />
                  검증 에러 있음
                </span>
              ) : (
                <span className="flex items-center gap-1 text-sm text-green-600">
                  <CheckCircle className="size-4" />
                  검증 통과
                </span>
              )}
            </div>

            <div className="rounded-xl border border-input overflow-auto max-h-[500px]">
              {activeTab === "quiz" ? (
                <QuizPreviewTable
                  rows={quizRows}
                  onUpdate={updateQuizRow}
                />
              ) : (
                <GeneralPreviewTable
                  rows={generalRows}
                  onUpdate={updateGeneralRow}
                />
              )}
            </div>

            <Button
              onClick={handleUpload}
              disabled={isPending || currentHasErrors}
              className="w-full"
              size="lg"
            >
              {isPending ? "업로드 중..." : `${currentRows.length}개 태스크 업로드`}
            </Button>
          </div>
        )}
      </div>
    </PageContainer>
  );
}

// ---------------------------------------------------------------------------
// 미리보기 테이블 컴포넌트
// ---------------------------------------------------------------------------

interface QuizPreviewTableProps {
  rows: QuizRow[];
  onUpdate: (id: number, field: keyof Omit<QuizRow, "id" | "errors" | "serverError">, value: string) => void;
}

function QuizPreviewTable({ rows, onUpdate }: QuizPreviewTableProps) {
  return (
    <table className="w-full text-xs">
      <thead className="bg-muted/50 sticky top-0">
        <tr>
          <th className="px-3 py-2 text-left font-medium w-8">#</th>
          <th className="px-3 py-2 text-left font-medium w-32">날짜</th>
          <th className="px-3 py-2 text-left font-medium w-28">반</th>
          <th className="px-3 py-2 text-left font-medium w-32">카테고리</th>
          <th className="px-3 py-2 text-left font-medium w-20">시작P</th>
          <th className="px-3 py-2 text-left font-medium w-20">종료P</th>
          <th className="px-3 py-2 text-left font-medium w-20">문제수</th>
          <th className="px-3 py-2 text-left font-medium">제목(선택)</th>
          <th className="px-3 py-2 text-left font-medium w-48">에러</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const hasIssue = row.errors.length > 0 || !!row.serverError;
          return (
            <tr
              key={row.id}
              className={`border-t ${hasIssue ? "bg-red-50 dark:bg-red-950/20" : ""}`}
            >
              <td className="px-3 py-1.5 text-muted-foreground">{row.id}</td>
              <td className="px-3 py-1.5">
                <Input
                  value={row.date}
                  onChange={(e) => onUpdate(row.id, "date", e.target.value)}
                  className="h-7 text-xs"
                  placeholder="YYYY-MM-DD"
                />
              </td>
              <td className="px-3 py-1.5">
                <Input
                  value={row.className}
                  onChange={(e) => onUpdate(row.id, "className", e.target.value)}
                  className="h-7 text-xs"
                />
              </td>
              <td className="px-3 py-1.5">
                <Input
                  value={row.categoryName}
                  onChange={(e) => onUpdate(row.id, "categoryName", e.target.value)}
                  className="h-7 text-xs"
                />
              </td>
              <td className="px-3 py-1.5">
                <Input
                  value={row.startPage}
                  onChange={(e) => onUpdate(row.id, "startPage", e.target.value)}
                  className="h-7 text-xs"
                  type="number"
                />
              </td>
              <td className="px-3 py-1.5">
                <Input
                  value={row.endPage}
                  onChange={(e) => onUpdate(row.id, "endPage", e.target.value)}
                  className="h-7 text-xs"
                  type="number"
                />
              </td>
              <td className="px-3 py-1.5">
                <Input
                  value={row.questionCount}
                  onChange={(e) => onUpdate(row.id, "questionCount", e.target.value)}
                  className="h-7 text-xs"
                  type="number"
                />
              </td>
              <td className="px-3 py-1.5">
                <Input
                  value={row.title}
                  onChange={(e) => onUpdate(row.id, "title", e.target.value)}
                  className="h-7 text-xs"
                />
              </td>
              <td className="px-3 py-1.5">
                {row.serverError && (
                  <p className="text-red-600 text-xs">[서버] {row.serverError}</p>
                )}
                {row.errors.map((err, i) => (
                  <p key={i} className="text-red-600 text-xs leading-relaxed">
                    {err}
                  </p>
                ))}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

interface GeneralPreviewTableProps {
  rows: GeneralRow[];
  onUpdate: (id: number, field: keyof Omit<GeneralRow, "id" | "errors" | "serverError">, value: string) => void;
}

function GeneralPreviewTable({ rows, onUpdate }: GeneralPreviewTableProps) {
  return (
    <table className="w-full text-xs">
      <thead className="bg-muted/50 sticky top-0">
        <tr>
          <th className="px-3 py-2 text-left font-medium w-8">#</th>
          <th className="px-3 py-2 text-left font-medium w-32">시작일</th>
          <th className="px-3 py-2 text-left font-medium w-32">종료일(선택)</th>
          <th className="px-3 py-2 text-left font-medium w-28">반</th>
          <th className="px-3 py-2 text-left font-medium">제목</th>
          <th className="px-3 py-2 text-left font-medium">설명(선택)</th>
          <th className="px-3 py-2 text-left font-medium w-48">에러</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const hasIssue = row.errors.length > 0 || !!row.serverError;
          return (
            <tr
              key={row.id}
              className={`border-t ${hasIssue ? "bg-red-50 dark:bg-red-950/20" : ""}`}
            >
              <td className="px-3 py-1.5 text-muted-foreground">{row.id}</td>
              <td className="px-3 py-1.5">
                <Input
                  value={row.date}
                  onChange={(e) => onUpdate(row.id, "date", e.target.value)}
                  className="h-7 text-xs"
                  placeholder="YYYY-MM-DD"
                />
              </td>
              <td className="px-3 py-1.5">
                <Input
                  value={row.endDate}
                  onChange={(e) => onUpdate(row.id, "endDate", e.target.value)}
                  className="h-7 text-xs"
                  placeholder="YYYY-MM-DD"
                />
              </td>
              <td className="px-3 py-1.5">
                <Input
                  value={row.className}
                  onChange={(e) => onUpdate(row.id, "className", e.target.value)}
                  className="h-7 text-xs"
                />
              </td>
              <td className="px-3 py-1.5">
                <Input
                  value={row.title}
                  onChange={(e) => onUpdate(row.id, "title", e.target.value)}
                  className="h-7 text-xs"
                />
              </td>
              <td className="px-3 py-1.5">
                <Input
                  value={row.description}
                  onChange={(e) => onUpdate(row.id, "description", e.target.value)}
                  className="h-7 text-xs"
                />
              </td>
              <td className="px-3 py-1.5">
                {row.serverError && (
                  <p className="text-red-600 text-xs">[서버] {row.serverError}</p>
                )}
                {row.errors.map((err, i) => (
                  <p key={i} className="text-red-600 text-xs leading-relaxed">
                    {err}
                  </p>
                ))}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export const Route = createFileRoute("/_content/tasks/upload")({
  component: TasksUploadPage,
});
